import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * Page size that fills the viewport instead of a hardcoded constant.
 *
 * A fixed row count only looks right at one window height: a tall screen ends
 * up with a band of empty space under the table, and a short one pushes the
 * pagination below the fold. This measures the rendered row height and the
 * space actually left under the table, then reports how many rows fit.
 *
 * Attach the returned `bodyRef` to the <tbody>, and list every element between
 * the table and the bottom of the page (pagination, footer) in `belowRefs` so
 * their heights are reserved. `gutter` covers whatever margins/padding those
 * refs don't account for.
 *
 * Phones keep the fixed `fallback`: mobile Safari grows and shrinks
 * `innerHeight` as its URL bar hides, so fitting there would reshuffle the
 * table under the reader's thumb mid-scroll.
 *
 * @param {number}   fallback  count to use until a measurement is possible
 * @param {Array}    belowRefs refs to elements that must stay on screen below
 * @param {number}   min/max   clamps for the computed count
 * @param {number}   gutter    extra px to keep free at the bottom
 * @param {number}   minWidth  narrower viewports keep `fallback`
 * @param {boolean}  enabled   false pins the count to `fallback`
 */
export default function useFitRows({
  fallback,
  belowRefs = [],
  min = 3,
  max = 40,
  gutter = 24,
  minWidth = 768,
  enabled = true,
}) {
  const bodyRef = useRef(null);
  const [rowsPerPage, setRowsPerPage] = useState(fallback);

  // `belowRefs` is a fresh array on every render; hold it in a ref so the
  // measure callback doesn't change identity each time.
  const belowRef = useRef(belowRefs);
  belowRef.current = belowRefs;

  // Last two counts we settled on, to catch a two-value flip-flop (see below).
  const historyRef = useRef([]);

  const measure = useCallback(() => {
    if (!enabled || window.innerWidth < minWidth) {
      setRowsPerPage(fallback);
      return;
    }
    const body = bodyRef.current;
    const first = body?.rows?.[0];
    // Empty and loading states render a single colSpan cell, which is nothing
    // like a data row — leave the count alone until there's a row to measure.
    if (!first || first.cells.length <= 1) return;

    const rowHeight = first.getBoundingClientRect().height;
    if (!rowHeight) return;

    const below = belowRef.current.reduce(
      (sum, ref) => sum + (ref?.current?.getBoundingClientRect().height ?? 0),
      0,
    );
    // The top of the tbody doesn't depend on how many rows are in it, so this
    // measurement is stable — no feedback loop with the count it produces.
    const room =
      window.innerHeight - body.getBoundingClientRect().top - below - gutter;
    const next = Math.max(min, Math.min(max, Math.floor(room / rowHeight)));

    // If two counts each imply the other (a row height that varies with which
    // rows are on screen, or pagination that appears at one count and not the
    // other), settle on the smaller so nothing spills past the fold.
    const [twoAgo, oneAgo] = historyRef.current.slice(-2);
    if (oneAgo != null && twoAgo === next && oneAgo !== next) {
      const settled = Math.min(next, oneAgo);
      historyRef.current = [settled, settled];
      setRowsPerPage(settled);
      return;
    }
    historyRef.current = [...historyRef.current.slice(-1), next];
    setRowsPerPage(next);
  }, [enabled, fallback, gutter, min, max, minWidth]);

  // No dependency array: re-measure after every render, so the count settles
  // once the new row set has actually laid out. React bails on an unchanged
  // state value, which is what stops this.
  useLayoutEffect(measure);

  useEffect(() => {
    let frame = 0;
    const onResize = () => {
      historyRef.current = [];
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      cancelAnimationFrame(frame);
    };
  }, [measure]);

  return { bodyRef, rowsPerPage };
}
